import { useState, useEffect } from 'react';
import { FileArchive, Loader2 } from 'lucide-react';
import { extractImagesFromZip } from '@/lib/imageUtils';
import ImageCard from './ImageCard';
import ImageLightbox, { type LightboxImage } from './ImageLightbox';

interface ZipImageViewerProps {
  zipUrl: string;
  title: string;
  compact?: boolean;
}

const IMAGES_PER_BATCH = 20;

export default function ZipImageViewer({ zipUrl, title, compact = false }: ZipImageViewerProps) {
  const [isExtracting, setIsExtracting] = useState(false);
  const [hasExtracted, setHasExtracted] = useState(false);
  const [extractError, setExtractError] = useState<string | null>(null);
  const [allImages, setAllImages] = useState<Array<{ filename: string; blobUrl: string }>>([]);
  const [displayCount, setDisplayCount] = useState(IMAGES_PER_BATCH);
  const [lightboxState, setLightboxState] = useState<{ images: LightboxImage[]; index: number } | null>(null);

  // Clean up blob URLs when component unmounts
  useEffect(() => {
    return () => {
      allImages.forEach((img) => {
        URL.revokeObjectURL(img.blobUrl);
      });
    };
  }, [allImages]);

  const handleExtract = async () => {
    setIsExtracting(true);
    setExtractError(null);

    try {
      const images = await extractImagesFromZip(zipUrl);
      setAllImages(images);
      setHasExtracted(true);

      if (images.length === 0) {
        setExtractError('No images found in ZIP file (checked root folder only)');
      }
    } catch (error) {
      console.error('Failed to extract ZIP:', error);
      setExtractError(
        error instanceof Error ? error.message : 'Failed to extract images from ZIP'
      );
    } finally {
      setIsExtracting(false);
    }
  };

  const handleLoadMore = () => {
    setDisplayCount((prev) => prev + IMAGES_PER_BATCH);
  };

  const visibleImages = allImages.slice(0, displayCount);
  const hasMore = displayCount < allImages.length;

  return (
    <>
      <div className="glass-card rounded-lg p-6 space-y-4">
        {/* Title and Extract Button */}
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold">{title}</h2>
          {!hasExtracted && (
            <button
              type="button"
              onClick={handleExtract}
              disabled={isExtracting}
              className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary/10 hover:bg-primary/20 text-primary border border-primary/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isExtracting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Extracting...
                </>
              ) : (
                <>
                  <FileArchive className="h-4 w-4" />
                  View images from ZIP
                </>
              )}
            </button>
          )}
        </div>

        {/* Error State */}
        {extractError && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
            <p className="text-sm text-red-400">{extractError}</p>
          </div>
        )}

        {/* Image Grid */}
        {hasExtracted && allImages.length > 0 && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {visibleImages.map((img, i) => (
                <ImageCard
                  key={img.blobUrl}
                  url={img.blobUrl}
                  compact={compact}
                  naturalLoading={false}
                  onOpenLightbox={() => setLightboxState({
                    images: visibleImages.map(v => ({ url: v.blobUrl, filename: v.filename })),
                    index: i,
                  })}
                />
              ))}
            </div>

            {/* Load More Button */}
            {hasMore && (
              <div className="flex justify-center pt-4">
                <button
                  type="button"
                  onClick={handleLoadMore}
                  className="px-4 py-2 rounded-md bg-primary/10 hover:bg-primary/20 text-primary border border-primary/30 transition-colors"
                >
                  Load more ({allImages.length - displayCount} remaining)
                </button>
              </div>
            )}

            {/* Results Summary */}
            <div className="text-sm text-muted-foreground text-center">
              Showing {visibleImages.length} of {allImages.length} images from ZIP
            </div>
          </>
        )}
      </div>

      {/* Lightbox */}
      {lightboxState && (
        <ImageLightbox
          images={lightboxState.images}
          currentIndex={lightboxState.index}
          isOpen={!!lightboxState}
          onClose={() => setLightboxState(null)}
          onNavigate={(index) => setLightboxState(prev => prev ? { ...prev, index } : null)}
        />
      )}
    </>
  );
}
